/**
 * 협력사 홈페이지 관련 타입 정의
 */

/**
 * 탭 타입
 */
export type SupplierTabType = 'settings' | 'weeklyScore' | 'dailyStatus' | 'riderStatus';

/**
 * 요일 타입
 */
export type DayOfWeek = 'wed' | 'thu' | 'fri' | 'sat' | 'sun' | 'mon' | 'tue';

/**
 * 시간대 슬롯 상태
 */
export type TimeSlotStatus = 'waiting' | 'inProgress' | 'completed';

/**
 * 자동 갱신 간격 타입
 */
export type AutoRefreshInterval = 'off' | '1min' | '5min' | '30min' | '1hour';

/**
 * 자동 갱신 옵션
 */
export interface AutoRefreshOption {
    value: AutoRefreshInterval;
    label: string;
    milliseconds: number;
}

/**
 * 등급 타입
 */
export type GradeType = '미사용' | '브론즈' | '실버' | '골드' | '다이아' | '마스터';

/**
 * 라이더 필터 타입
 */
export type RiderFilterType = '전체' | '아침점심' | '오후 논피크' | '저녁 피크' | '야간';

/**
 * 라이더 정렬 타입
 */
export type RiderSortType = '수락율' | '거절순' | '완료순' | '이름순';

/**
 * 주간 옵션 (수~화)
 */
export interface WeekOption {
    value: string;
    label: string;
    start: Date;
    end: Date;
}

/**
 * 날짜 옵션
 */
export interface DateOption {
    value: string;
    label: string;
}

/**
 * 라이더 상태
 */
export type RiderStatus = 'active' | 'inactive' | 'completed';

/**
 * 시간대 슬롯 타입
 */
export interface TimeSlot {
    id: string;
    name: string;
    icon: string;
    timeRange: string;
    current: number;
    target: number;
    rate: number;
    status: TimeSlotStatus;
}

/**
 * 요일별 시간대 데이터 타입
 */
export interface DayTimeSlotData {
    rate: number;           // 달성률 (%)
    count: number;          // 실제 수행 건수
    isAchieved: boolean;    // 달성 여부
}

/**
 * 요일별 데이터 타입
 */
export interface DayData {
    day: DayOfWeek;
    label: string;
    deliveryDate: string;   // YYYYMMDD 형식
    acceptanceRate: number;
    currentSlots: number;
    totalSlots: number;
    // 시간대별 달성률 및 달성 여부
    morningPeak: DayTimeSlotData;   // 아침/점심 피크
    afternoonNonPeak: DayTimeSlotData;  // 오후 논피크
    eveningPeak: DayTimeSlotData;       // 저녁 피크
    night: DayTimeSlotData;             // 야간
    hasData: boolean;                   // 데이터 존재 여부
}

/**
 * 라이더 데이터 타입
 */
export interface RiderData {
    id: string;
    name: string;
    status: RiderStatus;
    phoneNumber: string;
    completedCount: number;
    acceptanceRate: number;
    morningPeakCount: number;
    afternoonNonPeakCount: number;
    eveningPeakCount: number;
    nightCount: number;
}

/**
 * 협력사 정보 타입
 */
export interface SupplierInfo {
    supplierName: string;
}

/**
 * 날짜 범위 타입
 */
export interface DateRange {
    start: string;
    end: string;
}

/**
 * 주간 통계 타입
 */
export interface WeeklyStats {
    achievedSlots: number;
    totalSlots: number;
    completedCount: number;
    targetCount: number;
    acceptanceRate: number;
    bottomThreeAverage: number;
    isAchieved: boolean;
}

/**
 * 금일 통계 타입
 */
export interface DailyStats {
    completedCount: number;
    rejectedCount: number;
    acceptanceRate: number;
}

/**
 * 라이더 통계 타입
 */
export interface RiderStats {
    totalRiders: number;
    activeRiders: number;
    completedRiders: number;
}

/**
 * 설정 저장 요청 타입
 */
export interface SaveSettingsRequest {
    dateRange: DateRange;
    setCount: string;
    grade: string;
}

/**
 * 설정 저장 응답 타입
 */
export interface SaveSettingsResponse {
    success: boolean;
    message?: string;
}

/**
 * 주간 점수 조회 요청 타입
 */
export interface WeeklyScoreRequest {
    startDate: string;
    endDate: string;
}

/**
 * 주간 점수 조회 응답 타입
 */
export interface WeeklyScoreResponse {
    weeklyStats: WeeklyStats;
    dailyData: DayData[];
    success: boolean;
    message?: string;
}

/**
 * 금일 달성현황 조회 요청 타입
 */
export interface DailyStatusRequest {
    date: string;
}

/**
 * 금일 달성현황 조회 응답 타입
 */
export interface DailyStatusResponse {
    dailyStats: DailyStats;
    timeSlots: TimeSlot[];
    success: boolean;
    message?: string;
}

/**
 * 라이더 현황 조회 요청 타입
 */
export interface RiderStatusRequest {
    filter: string;
    sortBy: string;
    searchQuery?: string;
}

/**
 * 라이더 현황 조회 응답 타입
 */
export interface RiderStatusResponse {
    riderStats: RiderStats;
    riders: RiderData[];
    success: boolean;
    message?: string;
}

/**
 * 기본값 정의
 */
export const DEFAULT_WEEKLY_STATS: WeeklyStats = {
    achievedSlots: 0,
    totalSlots: 28,
    completedCount: 0,
    targetCount: 3750,
    acceptanceRate: 0,
    bottomThreeAverage: 0,
    isAchieved: false,
};

export const DEFAULT_DAILY_STATS: DailyStats = {
    completedCount: 0,
    rejectedCount: 0,
    acceptanceRate: 0,
};

export const DEFAULT_RIDER_STATS: RiderStats = {
    totalRiders: 0,
    activeRiders: 0,
    completedRiders: 0,
};

const DEFAULT_TIME_SLOT_DATA: DayTimeSlotData = { rate: 0, count: 0, isAchieved: false };

export const DEFAULT_DAILY_DATA: DayData[] = [
    { day: 'wed', label: '수요일', deliveryDate: '', acceptanceRate: 0, currentSlots: 0, totalSlots: 5, morningPeak: DEFAULT_TIME_SLOT_DATA, afternoonNonPeak: DEFAULT_TIME_SLOT_DATA, eveningPeak: DEFAULT_TIME_SLOT_DATA, night: DEFAULT_TIME_SLOT_DATA, hasData: false },
    { day: 'thu', label: '목요일', deliveryDate: '', acceptanceRate: 0, currentSlots: 0, totalSlots: 5, morningPeak: DEFAULT_TIME_SLOT_DATA, afternoonNonPeak: DEFAULT_TIME_SLOT_DATA, eveningPeak: DEFAULT_TIME_SLOT_DATA, night: DEFAULT_TIME_SLOT_DATA, hasData: false },
    { day: 'fri', label: '금요일', deliveryDate: '', acceptanceRate: 0, currentSlots: 0, totalSlots: 5, morningPeak: DEFAULT_TIME_SLOT_DATA, afternoonNonPeak: DEFAULT_TIME_SLOT_DATA, eveningPeak: DEFAULT_TIME_SLOT_DATA, night: DEFAULT_TIME_SLOT_DATA, hasData: false },
    { day: 'sat', label: '토요일', deliveryDate: '', acceptanceRate: 0, currentSlots: 0, totalSlots: 5, morningPeak: DEFAULT_TIME_SLOT_DATA, afternoonNonPeak: DEFAULT_TIME_SLOT_DATA, eveningPeak: DEFAULT_TIME_SLOT_DATA, night: DEFAULT_TIME_SLOT_DATA, hasData: false },
    { day: 'sun', label: '일요일', deliveryDate: '', acceptanceRate: 0, currentSlots: 0, totalSlots: 5, morningPeak: DEFAULT_TIME_SLOT_DATA, afternoonNonPeak: DEFAULT_TIME_SLOT_DATA, eveningPeak: DEFAULT_TIME_SLOT_DATA, night: DEFAULT_TIME_SLOT_DATA, hasData: false },
    { day: 'mon', label: '월요일', deliveryDate: '', acceptanceRate: 0, currentSlots: 0, totalSlots: 5, morningPeak: DEFAULT_TIME_SLOT_DATA, afternoonNonPeak: DEFAULT_TIME_SLOT_DATA, eveningPeak: DEFAULT_TIME_SLOT_DATA, night: DEFAULT_TIME_SLOT_DATA, hasData: false },
    { day: 'tue', label: '화요일', deliveryDate: '', acceptanceRate: 0, currentSlots: 0, totalSlots: 5, morningPeak: DEFAULT_TIME_SLOT_DATA, afternoonNonPeak: DEFAULT_TIME_SLOT_DATA, eveningPeak: DEFAULT_TIME_SLOT_DATA, night: DEFAULT_TIME_SLOT_DATA, hasData: false },
];

export const DEFAULT_TIME_SLOTS: TimeSlot[] = [
    {
        id: 'morning',
        name: '아침/점심 피크',
        icon: '🌅',
        timeRange: '평일: 06:00~12:59 / 주말: 06:00~13:59',
        current: 0,
        target: 105,
        rate: 0,
        status: 'inProgress',
    },
    {
        id: 'afternoon',
        name: '오후 논피크',
        icon: '☀️',
        timeRange: '평일: 13:00~16:59 / 주말: 14:00~16:59',
        current: 0,
        target: 100,
        rate: 0,
        status: 'inProgress',
    },
    {
        id: 'evening',
        name: '저녁 피크',
        icon: '🌆',
        timeRange: '17:00 ~ 19:59, 평일/주말 동일',
        current: 0,
        target: 150,
        rate: 0,
        status: 'inProgress',
    },
    {
        id: 'night',
        name: '야간',
        icon: '🌙',
        timeRange: '20:00 ~ 02:59, 평일/주말 동일',
        current: 0,
        target: 145,
        rate: 0,
        status: 'waiting',
    },
];

/**
 * 자동 갱신 옵션 목록
 */
export const AUTO_REFRESH_OPTIONS: AutoRefreshOption[] = [
    { value: 'off', label: '사용안함', milliseconds: 0 },
    { value: '1min', label: '1분', milliseconds: 60000 },
    { value: '5min', label: '5분', milliseconds: 300000 },
    { value: '30min', label: '30분', milliseconds: 1800000 },
    { value: '1hour', label: '1시간', milliseconds: 3600000 },
];

/**
 * 등급 옵션 목록
 */
export const GRADE_OPTIONS: GradeType[] = ['미사용', '브론즈', '실버', '골드', '다이아', '마스터'];

/**
 * 세트 수 옵션 생성 (미사용 ~ 15세트)
 */
export const SET_COUNT_OPTIONS: string[] = [
    '미사용',
    ...Array.from({ length: 15 }, (_, i) => `${i + 1}세트`),
];

/**
 * 라이더 필터 옵션 목록
 */
export const RIDER_FILTER_OPTIONS: RiderFilterType[] = [
    '전체',
    '아침점심',
    '오후 논피크',
    '저녁 피크',
    '야간',
];

/**
 * 라이더 정렬 옵션 목록
 */
export const RIDER_SORT_OPTIONS: RiderSortType[] = ['수락율', '거절순', '완료순', '이름순'];

/**
 * 피크 시간대 정의 (시작시간, 종료시간)
 * 심야는 다음날 새벽까지이므로 특별 처리 필요
 */
export const PEAK_TIME_RANGES = {
    morning: {
        weekday: { start: 6, end: 13 }, // 06:00 ~ 12:59
        weekend: { start: 6, end: 14 }, // 06:00 ~ 13:59
    },
    afternoon: {
        weekday: { start: 13, end: 17 }, // 13:00 ~ 16:59
        weekend: { start: 14, end: 17 }, // 14:00 ~ 16:59
    },
    evening: {
        weekday: { start: 17, end: 20 }, // 17:00 ~ 19:59
        weekend: { start: 17, end: 20 }, // 17:00 ~ 19:59
    },
    night: {
        weekday: { start: 20, end: 27 }, // 20:00 ~ 02:59 (27 = 다음날 03:00)
        weekend: { start: 20, end: 27 }, // 20:00 ~ 02:59
    },
};
